import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Trash2 } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import PurchaseOrderForm from '../../components/purchase/PurchaseOrderForm';
import ReceiveForm from '../../components/purchase/ReceiveForm';
import api from '../../lib/api';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error';
  const { data, status } = error.response;
  return data?.detail || data?.message || `Request failed (${status})`;
};

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showReceiveForm, setShowReceiveForm] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewingPO, setViewingPO] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) { navigate('/login'); return; }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get(`/purchase-orders${params}`);
      setOrders(res.data);
    } catch (err) {
      console.error('Error fetching POs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (po) => {
    if (!window.confirm(`Delete PO ${po.po_number}?`)) return;
    try {
      await api.delete(`/purchase-orders/${po.id}`);
      fetchOrders();
    } catch (err) {
      alert(getErrorMessage(err));
    }
  };

  const handleReceive = async (po) => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}`);
      setSelectedPO(res.data);
      setShowReceiveForm(true);
    } catch (err) {
      alert('Failed to load PO details');
    }
  };

  const handleView = async (po) => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}`);
      setViewingPO(res.data);
    } catch (err) {
      alert('Failed to load PO details');
    }
  };

  const statusColors = {
    draft: 'bg-[#F5F5F5] text-[#6b7280]',
    sent: 'bg-[#DBEAFE] text-[#1E40AF]',
    partially_received: 'bg-[#FEF3C7] text-[#92400E]',
    received: 'bg-[#DBEAFE] text-[#1E40AF]',
    cancelled: 'bg-[#FEE2E2] text-[#991B1B]',
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="purchase-orders-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="po-title">Purchase Orders</h1>
            <button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm px-4 py-2 font-medium flex items-center gap-2" data-testid="add-po-button">
              <Plus className="w-5 h-5" /> New PO
            </button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm mb-6 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1a1a2e] mb-2">Status Filter</label>
                <select className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="po-status-filter">
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="partially_received">Partially Received</option>
                  <option value="received">Received</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">Loading...</div>
          ) : orders.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center">
              <p className="text-[#6b7280] mb-4">No purchase orders found</p>
              <button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm px-4 py-2 font-medium">Create First PO</button>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[600px]" data-testid="po-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr>
                    <th className="py-3 px-4">PO #</th>
                    <th className="py-3 px-4">Supplier</th>
                    <th className="py-3 px-4">Order Date</th>
                    <th className="py-3 px-4">Items</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((po) => (
                    <tr key={po.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                      <td className="py-3 px-4 font-medium text-[#1a1a2e]">{po.po_number}</td>
                      <td className="py-3 px-4 text-[#1a1a2e]">{po.supplier?.name || 'N/A'}</td>
                      <td className="py-3 px-4 text-[#1a1a2e]">{po.order_date ? new Date(po.order_date).toLocaleDateString() : '-'}</td>
                      <td className="py-3 px-4 text-[#6b7280]">{po.items_count}</td>
                      <td className="py-3 px-4 font-medium text-[#1a1a2e]">Rs. {parseFloat(po.total_amount).toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${statusColors[po.status] || 'bg-gray-100'}`}>
                          {po.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => handleView(po)} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#1E40AF] text-[#1E40AF] text-xs font-medium gap-1" data-testid={`view-po-${po.id}`}>
                            <Eye className="w-3 h-3" /> View
                          </button>
                          {(po.status === 'draft' || po.status === 'sent' || po.status === 'partially_received') && (
                            <button onClick={() => handleReceive(po)} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#1E40AF] text-[#1E40AF] text-xs font-medium" data-testid={`receive-po-${po.id}`}>
                              Receive
                            </button>
                          )}
                          {po.status === 'draft' && (
                            <button onClick={() => handleDelete(po)} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#EF4444] text-[#EF4444] text-xs font-medium" data-testid={`delete-po-${po.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {showForm && <PurchaseOrderForm onClose={() => setShowForm(false)} onSuccess={() => { setShowForm(false); fetchOrders(); }} />}

      {showReceiveForm && selectedPO && (
        <ReceiveForm purchaseOrder={selectedPO} onClose={() => { setShowReceiveForm(false); setSelectedPO(null); }} onSuccess={() => { setShowReceiveForm(false); setSelectedPO(null); fetchOrders(); alert('Receipt created as draft. Go to Receipts to confirm and add stock.'); }} />
      )}

      {viewingPO && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="po-detail-modal">
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-[#e5e7eb] flex justify-between items-center shrink-0">
              <h2 className="text-xl font-semibold">{viewingPO.po_number}</h2>
              <button onClick={() => setViewingPO(null)} className="text-[#6b7280] hover:text-[#1a1a2e]" data-testid="close-po-detail">
                <span className="text-lg">&times;</span>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-sm">
                <div><span className="font-medium">Supplier:</span> {viewingPO.supplier?.name}</div>
                <div><span className="font-medium">Status:</span> <span className={`px-2 py-1 rounded-sm text-xs font-medium ${statusColors[viewingPO.status]}`}>{viewingPO.status.replace('_',' ')}</span></div>
                <div><span className="font-medium">Order Date:</span> {viewingPO.order_date ? new Date(viewingPO.order_date).toLocaleDateString() : '-'}</div>
                <div><span className="font-medium">GST Rate:</span> {viewingPO.gst_rate != null ? `${viewingPO.gst_rate}%` : 'Tenant Default'}</div>
              </div>
              <h3 className="font-semibold mb-2 text-sm">Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-[#e5e7eb] rounded-sm min-w-[400px]">
                <thead className="bg-[#f9fafb]">
                  <tr>
                    <th className="py-2 px-3 text-left">Product</th>
                    <th className="py-2 px-3 text-center">Ordered</th>
                    <th className="py-2 px-3 text-center">Received</th>
                    <th className="py-2 px-3 text-right">Unit Cost</th>
                    <th className="py-2 px-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingPO.items?.map((item, i) => (
                    <tr key={i} className="border-t border-[#e5e7eb]">
                      <td className="py-2 px-3">{item.product_name} ({item.product_sku})</td>
                      <td className="py-2 px-3 text-center">{item.quantity_ordered}</td>
                      <td className="py-2 px-3 text-center">{item.quantity_received}</td>
                      <td className="py-2 px-3 text-right">Rs. {parseFloat(item.unit_cost).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right">Rs. {parseFloat(item.total).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
              <div className="mt-3 text-sm space-y-1 border-t border-[#e5e7eb] pt-3">
                <div className="flex justify-between text-[#6b7280]"><span>Subtotal</span><span data-testid="po-detail-subtotal">Rs. {parseFloat(viewingPO.subtotal).toLocaleString()}</span></div>
                <div className="flex justify-between text-[#6b7280]"><span>GST ({viewingPO.gst_rate != null ? `${viewingPO.gst_rate}%` : '-'})</span><span data-testid="po-detail-gst-amount">Rs. {parseFloat(viewingPO.gst_amount).toLocaleString()}</span></div>
                <div className="flex justify-between font-bold text-[#1a1a2e] text-base border-t border-[#e5e7eb] pt-2 mt-1"><span>Grand Total</span><span data-testid="po-detail-total">Rs. {parseFloat(viewingPO.total_amount).toLocaleString()}</span></div>
              </div>
              {viewingPO.receipts?.length > 0 && (
                <>
                  <h3 className="font-semibold mt-4 mb-2 text-sm">Receipts</h3>
                  <ul className="text-sm space-y-1">
                    {viewingPO.receipts.map((r, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-medium">{r.receipt_number}</span>
                        <span className={`px-2 py-0.5 rounded-sm text-xs ${statusColors[r.status]}`}>{r.status}</span>
                        <span className="text-[#6b7280]">{r.receipt_date ? new Date(r.receipt_date).toLocaleDateString() : ''}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#e5e7eb] flex justify-end shrink-0">
              <button onClick={() => setViewingPO(null)} className="px-4 py-2 border border-[#e5e7eb] rounded-sm text-[#1a1a2e] hover:bg-[#F5F5F5] font-medium min-h-[44px] inline-flex items-center">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;
